/**
 * Chat Error Handling Utilities
 * 
 * Features:
 * - Standardized error message processing and formatting
 * - Session expiry detection and handling
 * - Error categorization and severity assessment
 * - Error recovery mechanisms and retry logic
 * 
 * Logic:
 * - Extracts meaningful error messages from different error types
 * - Detects specific error conditions like session expiry
 * - Provides consistent error formatting for UI display
 * - Implements error recovery strategies based on error type
 * 
 * Changes:
 * - Extracted from Chat.tsx component for better separation of concerns
 * - Added comprehensive error categorization and handling
 * - Created reusable error processing functions
 * - Standardized error message formatting
 */

export enum ErrorType {
  NETWORK = 'network',
  AUTH = 'auth',
  SESSION = 'session',
  VALIDATION = 'validation',
  UNKNOWN = 'unknown'
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface ProcessedError {
  message: string;
  type: ErrorType;
  severity: ErrorSeverity;
  isRecoverable: boolean;
  shouldRetry: boolean;
  autoHideDelay?: number;
}

/**
 * Processes and standardizes error messages
 * @param error Error object, string, or unknown value
 * @returns Standardized error message string
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unknown error occurred';
};

/**
 * Detects if an error is related to session expiry
 * @param error Error object or message
 * @returns True if error indicates session expiry
 */
export const isSessionExpiredError = (error: unknown): boolean => {
  const message = getErrorMessage(error).toLowerCase();
  return message.includes('session expired') ||
    message.includes('unauthorized') ||
    message.includes('token expired') ||
    message.includes('authentication failed');
};

/**
 * Detects if an error is network-related
 * @param error Error object or message
 * @returns True if error is network-related
 */
export const isNetworkError = (error: unknown): boolean => {
  const message = getErrorMessage(error).toLowerCase();
  return message.includes('network') ||
    message.includes('connection') ||
    message.includes('timeout') ||
    message.includes('fetch');
};

/**
 * Categorizes error type based on error content
 * @param error Error object or message
 * @returns Error type category
 */
export const categorizeError = (error: unknown): ErrorType => {
  if (isSessionExpiredError(error)) {
    return ErrorType.SESSION;
  }

  if (isNetworkError(error)) {
    return ErrorType.NETWORK;
  }

  const message = getErrorMessage(error).toLowerCase();

  if (message.includes('validation') || message.includes('invalid')) {
    return ErrorType.VALIDATION;
  }

  if (message.includes('auth') || message.includes('permission')) {
    return ErrorType.AUTH;
  }

  return ErrorType.UNKNOWN;
};

/**
 * Determines error severity based on error type and content
 * @param error Error object or message
 * @returns Error severity level
 */
export const getErrorSeverity = (error: unknown): ErrorSeverity => {
  const type = categorizeError(error);

  switch (type) {
    case ErrorType.SESSION:
    case ErrorType.AUTH:
      return ErrorSeverity.HIGH;
    case ErrorType.NETWORK:
      return ErrorSeverity.MEDIUM;
    case ErrorType.VALIDATION:
      return ErrorSeverity.LOW;
    default:
      return ErrorSeverity.MEDIUM;
  }
};

/**
 * Determines if an error is recoverable
 * @param error Error object or message
 * @returns True if error is recoverable
 */
export const isRecoverableError = (error: unknown): boolean => {
  const type = categorizeError(error);

  // Network errors and some validation errors are typically recoverable
  return type === ErrorType.NETWORK || type === ErrorType.VALIDATION;
};

/**
 * Determines if an error should trigger a retry
 * @param error Error object or message
 * @returns True if error should trigger retry
 */
export const shouldRetryError = (error: unknown): boolean => {
  const type = categorizeError(error);

  // Only retry network errors automatically
  return type === ErrorType.NETWORK;
};

/**
 * Gets auto-hide delay for error messages
 * @param error Error object or message
 * @returns Delay in milliseconds, or undefined for no auto-hide
 */
export const getAutoHideDelay = (error: unknown): number | undefined => {
  const type = categorizeError(error);

  switch (type) {
    case ErrorType.SESSION:
      return 5000; // 5 seconds for session errors
    case ErrorType.VALIDATION:
      return 3000; // 3 seconds for validation errors
    case ErrorType.NETWORK:
      return 4000; // 4 seconds for network errors
    default:
      return undefined; // No auto-hide for other errors
  }
};

/**
 * Processes an error and returns comprehensive error information
 * @param error Error object or message
 * @returns Processed error with categorization and handling info
 */
export const processError = (error: unknown): ProcessedError => {
  const message = getErrorMessage(error);
  const type = categorizeError(error);
  const severity = getErrorSeverity(error);
  const isRecoverable = isRecoverableError(error);
  const shouldRetry = shouldRetryError(error);
  const autoHideDelay = getAutoHideDelay(error);

  return {
    message,
    type,
    severity,
    isRecoverable,
    shouldRetry,
    autoHideDelay
  };
};

/**
 * Formats error message for user display
 * @param error Error object or message
 * @returns User-friendly error message
 */
export const formatErrorForDisplay = (error: unknown): string => {
  const processed = processError(error);

  // Return user-friendly messages for common error types
  switch (processed.type) {
    case ErrorType.SESSION:
      return 'Your session has expired. Please refresh the page and try again.';
    case ErrorType.NETWORK:
      return 'Network connection error. Please check your connection and try again.';
    case ErrorType.AUTH:
      return 'Authentication error. Please refresh the page and try again.';
    case ErrorType.VALIDATION:
      return processed.message; // Validation messages are usually user-friendly
    default:
      return processed.message;
  }
};
